import { formatDate } from "@angular/common";
import { CustomerNotificationModel, HolidayModel, VacationRequestBasicModel, VacationRequestConflictModel, VacationRequestExtendedModel, VacationRequestModel, VacationRequestSimplifiedBasicModel } from "@shared/models";
import { Assignment, Customer, CustomerNotification, Employee, ExternalEmployee, InternalEmployee, Weeks } from "@shared/factories";
import { FormatDatesService } from "@shared/services";

export class VacationRequestSimplifiedBasic {
  id:                  number;

  leaveType:           string;
  leaveTypeMapped:     string;

  startsOn:            Date;
  endsOn:              Date;
  calendarWeek:        string;
  date:                string;

  totalVacationDays:   number;
  workingVacationDays: number;
  constructor(data: VacationRequestSimplifiedBasicModel) {
    this.id                  = data.id         ? data.id         : null;
    this.leaveType           = data.leave_type ? data.leave_type : null;
    this.leaveTypeMapped     = this.mapLeaveType(data.leave_type);

    this.startsOn            = FormatDatesService.parseDate(data.starts_on);
    this.endsOn              = FormatDatesService.parseDate(data.ends_on);
    this.calendarWeek        = `KW ${Weeks.getWeekNumber(this.startsOn) > 52 ? '01': Weeks.getWeekNumber(this.startsOn)}`;
    this.date                = `${formatDate(this.startsOn, 'dd.MM.yyyy','de')} - ${formatDate(this.endsOn, 'dd.MM.yyyy', 'de')}`;

    this.totalVacationDays   = Math.round((this.endsOn.getTime() - this.startsOn.getTime()) / (24*60*60*1000))+1;
    this.workingVacationDays = this.getWorkingVacationDays(this.startsOn, this.endsOn);
  }

  private mapLeaveType(type: string): string {
    switch (type) {
      case 'annual':   { return 'Tarifurlaub';  };
      case 'special':  { return 'Sonderurlaub'; };
      case 'unpaid':   { return 'Unbezahlt';    };
      case 'overtime': { return 'AZK';          };
      default:         { return 'N/A';          };
    };
  }

  private getWorkingVacationDays(from: Date, to: Date): number {
    let count = 0;
    for (let start = new Date(from); start.getTime() <= to.getTime(); start.setDate(start.getDate()+1)) {
      if (start.getDay() > 0 && start.getDay() < 6) count++;
    }
    return count;
  }

  toJSON(): VacationRequestSimplifiedBasicModel {
    return ({
      id:         this.id        ? this.id        : null,
      leave_type: this.leaveType ? this.leaveType : null,

      starts_on:  FormatDatesService.parseDateISO(this.startsOn),
      ends_on:    FormatDatesService.parseDateISO(this.endsOn),
    });
  }

}

export class VacationRequestConflict extends VacationRequestSimplifiedBasic {
  state:               string;
  mappedStateCustomer: string;
  mappedStateInternal: string;

  assignment:          Assignment;
  externalEmployee:    Employee;
  constructor(data: VacationRequestConflictModel) {
    super(data);

    this.state                = data.state             ? data.state                                   : null;
    this.mappedStateCustomer  = this.mapStateCustomer(data.state);
    this.mappedStateInternal  = this.mapStateInternal(data.state);

    this.assignment           = data.assignment        ? new Assignment(data.assignment)              : null;
    this.externalEmployee     = data.external_employee ? new ExternalEmployee(data.external_employee) : null;
  }

  toJSON(): VacationRequestConflictModel {
    return Object.assign(super.toJSON(), {
      state:             this.state            ? this.state                     : null,
      assignment:        this.assignment       ? this.assignment.toJSON()       : null,
      external_employee: this.externalEmployee ? this.externalEmployee.toJSON() : null,
    });
  }

  private mapStateCustomer(state: string): string {
    switch (state) {
      case 'approved_by_internal':       { return 'Bereits freigegeben'; };
      case 'approved_by_external':
      case 'approved_by_customer':       { return 'Freigabe vorhanden';  };
      case 'awaiting_external_approval':
      case 'awaiting_customer_approval': { return 'Freigabe erwartet';   };
      case 'rejected_by_external':
      case 'rejected_by_customer':       { return 'Freigabe abgelehnt';  };
    };
  }

  private mapStateInternal(state: string): string {
    switch (state) {
      case 'approved_by_internal':       { return 'Bereits freigegeben';          };
      case 'awaiting_customer_approval': { return 'Kundenfreigabe erwartet';      };
      case 'approved_by_customer':       { return 'Kundenfreigabe vorhanden';     };
      case 'approved_by_external':
      case 'awaiting_internal_approval': { return 'Freigabe erwartet';            };
      case 'rejected_by_customer':       { return 'Kunde gibt nicht frei';        };
      case 'awaiting_external_approval': { return 'Mitarbeiterfreigabe erwartet'; };
      case 'rejected_by_external':       { return 'MA gibt nicht frei';           };
    };
  }

}

export class VacationRequestBasic extends VacationRequestSimplifiedBasic {
  createdAt:           Date;
  createdBy:           Employee;

  reason:              string;
  customReason:        string;
  reasonMapped:        string;

  state:               string;

  approvedByInternal:  boolean;
  rejectedByInternal:  boolean;
  archivedByInternal:  boolean;
  archivedByExternal:  boolean;
  archivedBySystem:    boolean;

  mappedStateCustomer: string;
  mappedStateInternal: string;
  constructor(data: VacationRequestBasicModel) {
    super(data);
    this.createdAt           = FormatDatesService.parseDate(data.created_at);
    this.createdBy           = data.created_by    ? new Employee(data.created_by) : null;

    this.reason              = data.reason        ? data.reason                   : null;
    this.customReason        = data.custom_reason ? data.custom_reason            : null;
    this.reasonMapped        = this.mapReason(data.reason, data.custom_reason);

    this.state               = data.state         ? data.state                    : null;

    this.approvedByInternal  = this.state === 'approved_by_internal';
    this.rejectedByInternal  = this.state === 'rejected_by_internal';
    this.archivedByInternal  = this.state === 'archived_by_internal';
    this.archivedByExternal  = this.state === 'archived_by_external';
    this.archivedBySystem    = this.state === 'archived_by_system';

    this.mappedStateCustomer = this.mapStateCustomer(this.state);
    this.mappedStateInternal = this.mapStateInternal(this.state);
  }

  private mapReason(reason: string, custom_reason: string): string {
    switch (reason) {
      case 'wedding_birth':            { return 'Hochzeit oder Geburt';     };
      case 'wedding':                  { return 'Hochzeit';                 };
      case 'death':                    { return 'Todesfall';                };
      case 'death_of_close_relatives': { return 'Tod von nahen Verwandten'; };
      case 'death_of_relatives':       { return 'Tod von Verwandten';       };
      case 'birth':                    { return 'Geburt';                   };
      case 'other':                    { return 'Sonstiges';                };
      default:                         { return custom_reason;              };
    };
  }

  private mapStateCustomer(state: string): string {
    switch (state) {
      case 'approved_by_external':
      case 'approved_by_customer':       { return 'Freigabe vorhanden'; };
      case 'awaiting_external_approval':
      case 'awaiting_customer_approval': { return 'Freigabe erwartet';  };
      case 'rejected_by_external':
      case 'rejected_by_customer':       { return 'Freigabe abgelehnt'; };
    };
  }

  private mapStateInternal(state: string): string {
    switch (state) {
      case 'awaiting_customer_approval': { return 'Kundenfreigabe erwartet';      };
      case 'approved_by_customer':       { return 'Kundenfreigabe vorhanden';     };
      case 'approved_by_external':
      case 'awaiting_internal_approval': { return 'Freigabe erwartet';            };
      case 'rejected_by_customer':       { return 'Kunde gibt nicht frei';        };
      case 'awaiting_external_approval': { return 'Mitarbeiterfreigabe erwartet'; };
      case 'rejected_by_external':       { return 'MA gibt nicht frei';           };
    };
  }

  toJSON(): VacationRequestBasicModel {
    return Object.assign(super.toJSON(), {
      created_at: FormatDatesService.parseDateISO(this.createdAt),
      created_by:    this.createdBy    ? this.createdBy.toJSON() : null,
      reason:        this.reason       ? this.reason             : null,
      custom_reason: this.customReason ? this.customReason       : null,
      state:         this.state        ? this.state              : null,
      
    });
  }

}

export class VacationRequest extends VacationRequestBasic {
  assignment:                    Assignment;
  holidays:                      HolidayModel[];
  signer:                        string;

  externalEmployee:              ExternalEmployee;
  nameReverse:                   string;

  externalReview:                string;
  externalReviewedAt:            Date;

  creationInitiator:             string;
  initiatorMapped:               string;

  customerReview:                string;
  customerReviewedAt:            Date;
  customerRejectionReason:       string;
  customerReviewComment:         string;
  customerRejectionReasonMapped: string;

  precheckedByInternalAt:        Date;
  internalReviewedAt:            Date;
  internalReview:                string;
  internalRejectionReason:       string;
  internalReviewComment:         string;
  internalRejectionReasonMapped: string;

  daysBeforeAutoArchive:         number;
  autoArchiveAt:                 Date;
  archivedBySystemAt:            Date;
  archivedAt:                    Date;
  releasedAt:                    Date;

  exportState:                   string;
  exportComment:                 string;

  createdByInternal:             boolean;
  awaitingExternalReview:        boolean;
  exportResolved:                boolean;

  readOnlyForCustomer:           boolean;
  readOnlyForInternal:           boolean;

  belongsToInternalLocations:    boolean;

  constructor(data: VacationRequestModel) {
    super(data);

    this.assignment                    = data.assignment                ? new Assignment(data.assignment)              : null;
    this.holidays                      = data.holidays                  ? data.holidays                                : null;
    this.signer                        = data.signer                    ? data.signer                                  : null;

    this.externalEmployee              = data.external_employee         ? new ExternalEmployee(data.external_employee) : null;
    this.nameReverse                   = data.external_employee?.last_name  + ', ' + data.external_employee?.first_name;

    this.creationInitiator             = data.creation_initiator        ? data.creation_initiator                      : null;
    this.initiatorMapped               = this.mapInitiator(data.creation_initiator);

    this.externalReview                = data.external_review           ? data.external_review                         : null;
    this.externalReviewedAt            = FormatDatesService.parseDate(data.external_reviewed_at);

    this.customerReview                = data.customer_review           ? data.customer_review                         : null;
    this.customerReviewedAt            = FormatDatesService.parseDate(data.customer_reviewed_at);
    this.customerRejectionReason       = data.customer_rejection_reason ? data.customer_rejection_reason               : null;
    this.customerReviewComment         = data.customer_review_comment   ? data.customer_review_comment                 : null;
    this.customerRejectionReasonMapped = this.mapRejectReason(data.customer_rejection_reason);

    this.precheckedByInternalAt        = FormatDatesService.parseDate(data.prechecked_by_internal_at);
    this.internalReview                = data.internal_review           ? data.internal_review                         : null;
    this.internalReviewedAt            = FormatDatesService.parseDate(data.internal_reviewed_at);
    this.internalRejectionReason       = data.internal_rejection_reason ? data.internal_rejection_reason               : null;
    this.internalReviewComment         = data.internal_review_comment   ? data.internal_review_comment                 : null;
    this.internalRejectionReasonMapped = this.mapRejectReason(data.internal_rejection_reason);

    this.daysBeforeAutoArchive         = data.days_before_auto_archive;
    this.autoArchiveAt                 = this.parceAutoArchiveAt();
    this.archivedBySystemAt            = FormatDatesService.parseDate(data.archived_by_system_at);

    if (this.archivedBySystem  ) this.archivedAt = FormatDatesService.parseDate(this.archivedBySystemAt);
    if (this.archivedByInternal) this.archivedAt = FormatDatesService.parseDate(this.internalReviewedAt);
    if (this.archivedByExternal) this.archivedAt = FormatDatesService.parseDate(this.externalReviewedAt);
    
    if (this.approvedByInternal) this.releasedAt = FormatDatesService.parseDate(this.internalReviewedAt);
    if (this.rejectedByInternal) this.releasedAt = FormatDatesService.parseDate(this.internalReviewedAt);
    if (this.archivedAt        ) this.releasedAt = FormatDatesService.parseDate(this.archivedAt);
  
    this.exportState                   = data.export_state              ? data.export_state                            : null;
    this.exportComment                 = data.export_comment            ? data.export_comment                          : null;

    this.createdByInternal             = this.createdBy?.userGroup === 'Internal Employee';
    this.awaitingExternalReview        = this.state === 'awaiting_external_approval';
    this.exportResolved                = this.exportState === 'resolved_manually' || this.exportState === 'resolved_by_system';

    this.readOnlyForCustomer           = !!this.archivedAt || this.createdByInternal || !this.assignment || !!this.customerReview || !!this.internalReviewedAt;
    this.readOnlyForInternal           = !!this.archivedAt || !!this.internalReviewedAt;

    this.belongsToInternalLocations    = data.belongs_to_internal_locations ? data.belongs_to_internal_locations : null;
  }

  private parceAutoArchiveAt(): Date {
    if (this.daysBeforeAutoArchive === null) return null;
    if (isNaN(this.daysBeforeAutoArchive))   return null;
    let date = new Date();
    date.setDate(date.getDate() + this.daysBeforeAutoArchive);
    date.setHours(0,0,0);
    return date;
  }

  private mapInitiator(initiator: string): string {
    switch (initiator) {
      case 'internal': { return 'Veranlassung durch Arbeitgeber (SB / Disponent)'; };
      case 'external': { return 'Veranlassung durch Arbeitnehmer (MA)';            };
    };
  }

  private mapRejectReason(reason: string): string {
    switch (reason) {
      case 'incorrect_dates':       { return 'Falsche Daten';           };
      case 'no_vacation_days_left': { return 'Keine Urlaubstage übrig'; };
      case 'internal_reasons':      { return 'Interne Gründe';          };
      case 'rejected_by_external':  { return 'MA gibt nicht frei';      };
      case 'obsolete':              { return 'veraltete Daten';         };
      default:
        break;
    };
  }


  toJSON(): VacationRequestModel {
    return Object.assign(super.toJSON(), {
      assignment:                this.assignment              ? this.assignment.toJSON()       : null,
      holidays:                  this.holidays                ? this.holidays                  : null,
      signer:                    this.signer                  ? this.signer                    : null,
      external_employee:         this.externalEmployee        ? this.externalEmployee.toJSON() : null,

      creation_initiator:        this.creationInitiator       ? this.creationInitiator         : null,

      external_review:           this.externalReview          ? this.externalReview            : null,
      external_reviewed_at:      FormatDatesService.parseDateISO(this.externalReviewedAt),

      customer_review:           this.customerReview          ? this.customerReview            : null,
      customer_reviewed_at:      FormatDatesService.parseDateISO(this.customerReviewedAt),
      customer_review_comment:   this.customerReviewComment   ? this.customerReviewComment     : null,
      customer_rejection_reason: this.customerRejectionReason ? this.customerRejectionReason   : null,

      prechecked_by_internal_at: FormatDatesService.parseDateISO(this.precheckedByInternalAt),
      internal_review:           this.internalReview          ? this.internalReview            : null,
      internal_reviewed_at:      FormatDatesService.parseDateISO(this.internalReviewedAt),
      internal_rejection_reason: this.internalRejectionReason ? this.internalRejectionReason   : null,
      internal_review_comment:   this.internalReviewComment   ? this.internalReviewComment     : null,

      archived_by_system_at:     FormatDatesService.parseDateISO(this.archivedBySystemAt),
      days_before_auto_archive:  this.daysBeforeAutoArchive   ? this.daysBeforeAutoArchive     : null,

      export_state:              this.exportState             ? this.exportState               : null,
      export_comment:            this.exportComment           ? this.exportComment             : null,

      belongs_to_internal_locations: this.belongsToInternalLocations ? this.belongsToInternalLocations : null,
    });
  }

}

export class VacationRequestExtended extends VacationRequest {
  involvedAssignments:        Assignment[];
  customerSignatureUrl:       string;

  externalComment:            string;
  internalComment:            string;

  reviewedByExternal:         ExternalEmployee;
  reviewedByCustomer:         Customer;
  reviewedByInternal:         InternalEmployee;
  precheckedByInternal:       InternalEmployee;

  failedErpReport:            boolean;

  approvedByExternal:         boolean;
  rejectedByExternal:         boolean;

  customerNotifications:      CustomerNotification[];
  constructor(data: VacationRequestExtendedModel, notifications: CustomerNotificationModel[] = null) {
    super(data);

    this.involvedAssignments        = data.involved_assignments?.length  ? data.involved_assignments.map(a => new Assignment(a)) : null;
    this.customerSignatureUrl       = data.customer_signature_url        ? data.customer_signature_url                           : null;

    this.externalComment            = data.external_comment              ? data.external_comment                                 : null;
    this.internalComment            = data.internal_comment              ? data.internal_comment                                 : null;

    this.reviewedByExternal         = data.reviewed_by_external          ? new ExternalEmployee(data.reviewed_by_external)       : null;
    this.reviewedByCustomer         = data.reviewed_by_customer          ? new Customer(data.reviewed_by_customer)               : null;
    this.reviewedByInternal         = data.reviewed_by_internal          ? new InternalEmployee(data.reviewed_by_internal)       : null;
    this.precheckedByInternal       = data.prechecked_by_internal        ? new InternalEmployee(data.prechecked_by_internal)     : null;

    this.customerNotifications      = notifications ? notifications.map(n => new CustomerNotification(n)).sort((a,b) => b.created_at.getTime() - a.created_at.getTime()) : null;

    this.failedErpReport            = this.belongsToInternalLocations && this.internalReview && this.exportState === 'failed_export';

    this.approvedByExternal         = data.external_review === 'approved';
    this.rejectedByExternal         = data.external_review === 'rejected';

    this.readOnlyForInternal        = this.readOnlyForInternal  || !this.belongsToInternalLocations;
  }

  toJSON(): VacationRequestModel {
    return Object.assign(super.toJSON(), {
      involved_assignments:          this.involvedAssignments        ? this.involvedAssignments.map(a => a.toJSON()) : null,
      holidays:                      this.holidays                   ? this.holidays                                 : null,

      customer_signature_url:        this.customerSignatureUrl       ? this.customerSignatureUrl                     : null,

      external_comment:              this.externalComment            ? this.externalComment                          : null,
      internal_comment:              this.internalComment            ? this.internalComment                          : null,

      reviewed_by_external:          this.reviewedByExternal         ? this.reviewedByExternal.toJSON()              : null,
      reviewed_by_customer:          this.reviewedByCustomer         ? this.reviewedByCustomer.toJSON()              : null,
      reviewed_by_internal:          this.reviewedByInternal         ? this.reviewedByInternal.toJSON()              : null,
      prechecked_by_internal:        this.precheckedByInternal       ? this.precheckedByInternal.toJSON()            : null,

      belongs_to_internal_locations: this.belongsToInternalLocations ? this.belongsToInternalLocations               : null,
    });
  }

}
